import { useState, useCallback, useMemo } from 'react';

export interface IUseMySelectionCollectionValue<T, K extends string | number = string> {
  selectedItems: T[],
  selectedIds: Set<K>,
  addItem: (item: T) => void,
  removeItem: (id: K) => void,
  toggleItem: (item: T) => void,
  setItemsSelected: (items: T[], isSelected: boolean) => void,
  clearSelection: () => void,
  replaceItems: (updates: ReadonlyMap<K, T>) => void,
  selectionCount: number,
}

const useMySelectionCollection = <T, K extends string | number = string>(
  getId: (item: T) => K,
): IUseMySelectionCollectionValue<T, K> => {
  const [selectedItems, setSelectedItems] = useState<T[]>([]);

  const addItem = useCallback((item: T): void => {
    setSelectedItems((prev) => {
      const id = getId(item);
      if (prev.some((existing) => getId(existing) === id)) return prev;
      return [...prev, item];
    });
  }, [getId]);

  const removeItem = useCallback((id: K): void => {
    setSelectedItems((prev) => {
      if (!prev.some((existing) => getId(existing) === id)) return prev;
      return prev.filter((existing) => getId(existing) !== id);
    });
  }, [getId]);

  const toggleItem = useCallback((item: T): void => {
    setSelectedItems((prev) => {
      const id = getId(item);
      return prev.some((existing) => getId(existing) === id)
        ? prev.filter((existing) => getId(existing) !== id)
        : [...prev, item];
    });
  }, [getId]);

  const setItemsSelected = useCallback((items: T[], isSelected: boolean): void => {
    setSelectedItems((prev) => {
      if (isSelected) {
        const knownIds = new Set(prev.map(getId));
        const added = items.filter((item) => !knownIds.has(getId(item)));

        return added.length ? [...prev, ...added] : prev;
      }

      const removedIds = new Set(items.map(getId));
      const next = prev.filter((item) => !removedIds.has(getId(item)));

      return next.length === prev.length ? prev : next;
    });
  }, [getId]);

  const clearSelection = useCallback((): void => {
    setSelectedItems([]);
  }, []);

  const replaceItems = useCallback((updates: ReadonlyMap<K, T>): void => {
    if (updates.size === 0) return;
    setSelectedItems((prev) => prev.map((item) => updates.get(getId(item)) ?? item));
  }, [getId]);

  const selectedIds = useMemo(() => new Set(selectedItems.map(getId)), [selectedItems, getId]);

  return useMemo<IUseMySelectionCollectionValue<T, K>>(
    () => ({
      selectedItems,
      selectedIds,
      addItem,
      removeItem,
      toggleItem,
      setItemsSelected,
      clearSelection,
      replaceItems,
      selectionCount: selectedItems.length,
    }),
    [selectedItems, selectedIds, addItem, removeItem, toggleItem, setItemsSelected, clearSelection, replaceItems],
  );
};

export default useMySelectionCollection;
